import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import { useTranslationApp } from "../../../lib/i18next";
import { getIsBlockedActions } from "../../../actions/getters";
import { getKeyDatabase } from "../../../services/public";
import { startProduceCreativeTemplate } from "../../../actions/creativeTemplates";
import {
  startGetCreativeTemplateProduce,
  startListenerCreativeTemplateProduce,
  stopListenerCreativeTemplateProduce,
} from "../../../actions/creativeTemplatesProduce";
import { useCreditActionCosts } from "../../../hooks/useCreditActionCosts";
import { useAdvertiserContentLanguage } from "../../../hooks/useAdvertiserContentLanguage";
import { CONTENT_LANGUAGE_PRESET } from "../../../utils/catalogs/contentLanguage";
import {
  ALERTS_ASYNC_STATUS,
  CREATIVE_TEMPLATE_PRODUCE_INTENT,
  CREATIVE_TEMPLATE_PRODUCE_STATUS,
} from "../../../utils/constants";
import { CREATIVE_ALERT_TYPE } from "../../../utils/creativeJobAlertConfig";
import { SimpleAlert } from "../../../components/Alerts/Alerts";
import {
  TEMPLATES_EDITOR_PREVIEW_ERROR,
  TEMPLATES_PRODUCE_VALIDATION_NO_FORMATS,
} from "../../../i18n/keysTranslations";
import {
  createVariationRows,
  getDefaultProduceLanguage,
  hasActiveProduceRun,
  listPreviewVariableEntries,
  listProduceColumns,
  listProduceCreatives,
} from "../produce/produceRows";
import { listArtboardIDs } from "../utils/artboards";
import { countNodes } from "../utils/layerTree";
import {
  getProduceSlideCount,
  isCarouselLayout,
  isContinuousLayout,
  listAllCells,
} from "../utils/slides";

export const TEMPLATE_EDITOR_PREVIEW_SCOPE = {
  SLIDE: "slide",
  CAROUSEL: "carousel",
};

const resolvePreviewScope = (scope) => {
  switch (scope) {
    case TEMPLATE_EDITOR_PREVIEW_SCOPE.SLIDE:
      return TEMPLATE_EDITOR_PREVIEW_SCOPE.SLIDE;
    case TEMPLATE_EDITOR_PREVIEW_SCOPE.CAROUSEL:
      return TEMPLATE_EDITOR_PREVIEW_SCOPE.CAROUSEL;
    default: {
      const exhaustive = scope;
      throw new Error(`Unhandled preview scope: ${exhaustive}`);
    }
  }
};

const listPreviewSlideIDs = ({ context, scope }) => {
  const carousel = isCarouselLayout({
    layoutKind: context?.layoutKind,
    slides: context?.slides,
  });
  if (!carousel) return [];
  switch (scope) {
    case TEMPLATE_EDITOR_PREVIEW_SCOPE.SLIDE: {
      const active = String(context?.activeSlideID || "").trim();
      return active ? [active] : [];
    }
    case TEMPLATE_EDITOR_PREVIEW_SCOPE.CAROUSEL: {
      const artboardID = String(context?.activeArtboardID || "").trim();
      return listAllCells({
        layoutKind: context.layoutKind,
        slides: context.slides,
        artboards: context.artboards,
        artboardIDs: artboardID ? [artboardID] : [],
      })
        .filter(
          (cell) => countNodes({ nodes: cell?.artboard?.nodes || {} }) > 0
        )
        .map((cell) => String(cell?.slideID || "").trim())
        .filter(Boolean);
    }
    default: {
      const exhaustive = scope;
      throw new Error(`Unhandled preview scope: ${exhaustive}`);
    }
  }
};

const TERMINAL_PRODUCE_STATUSES = new Set([
  CREATIVE_TEMPLATE_PRODUCE_STATUS.SUCCESS,
  CREATIVE_TEMPLATE_PRODUCE_STATUS.PARTIAL,
  CREATIVE_TEMPLATE_PRODUCE_STATUS.ERROR,
]);

const TERMINAL_ALERT_STATUSES = new Set([
  ALERTS_ASYNC_STATUS.SUCCESS,
  ALERTS_ASYNC_STATUS.ERROR,
]);

const listPreviewUrls = (produceRecord) =>
  listProduceCreatives({
    creatives: produceRecord?.creatives,
  })
    .map((item) => String(item?.url || "").trim())
    .filter(Boolean);

const isProduceRecordTerminal = (produceRecord) =>
  Boolean(produceRecord) &&
  !hasActiveProduceRun({ produceRecord }) &&
  TERMINAL_PRODUCE_STATUSES.has(produceRecord.status);

const EMPTY_NOTIFICATIONS = [];

const findPreviewProduceAlert = ({ notifications, produceID }) =>
  notifications.find(
    (alert) =>
      alert?.type === CREATIVE_ALERT_TYPE.producingCreativeTemplate &&
      alert?.generationData?.produceID === produceID
  );

const PREVIEW_RUN_KEY = {
  CAROUSEL: "__carousel__",
  SINGLE: "__single__",
  RESTORE: "__restore__",
};

const GLOBAL_PREVIEW_RUN_KEYS = new Set([
  PREVIEW_RUN_KEY.CAROUSEL,
  PREVIEW_RUN_KEY.RESTORE,
]);

const isGlobalPreviewRunKey = (key) => GLOBAL_PREVIEW_RUN_KEYS.has(key);

const previewRunKey = ({ scope, context }) => {
  switch (scope) {
    case TEMPLATE_EDITOR_PREVIEW_SCOPE.CAROUSEL:
      return PREVIEW_RUN_KEY.CAROUSEL;
    case TEMPLATE_EDITOR_PREVIEW_SCOPE.SLIDE: {
      const carousel = isCarouselLayout({
        layoutKind: context?.layoutKind,
        slides: context?.slides,
      });
      if (!carousel) return PREVIEW_RUN_KEY.SINGLE;
      return (
        String(context?.activeSlideID || "").trim() || PREVIEW_RUN_KEY.SINGLE
      );
    }
    default: {
      const exhaustive = scope;
      throw new Error(`Unhandled preview scope: ${exhaustive}`);
    }
  }
};

const canStartPreviewRun = ({ keys, key }) => {
  if ([...keys].some((item) => isGlobalPreviewRunKey(item))) return false;
  if (isGlobalPreviewRunKey(key) && keys.size > 0) return false;
  return !keys.has(key);
};

export const isTemplateEditorPreviewLoading = ({
  previewingKeys = [],
  activeSlideID = "",
  isCarousel = false,
} = {}) => {
  if (previewingKeys.some((key) => isGlobalPreviewRunKey(key))) return true;
  const activeKey = isCarousel
    ? String(activeSlideID || "").trim()
    : PREVIEW_RUN_KEY.SINGLE;
  return Boolean(activeKey) && previewingKeys.includes(activeKey);
};

const useTemplateEditorPreview = ({
  templateID,
  getPreviewContext,
  saveTemplate,
  isLocked = false,
  initialPreviewProduceID = "",
  previewRequestKey = "",
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const notifications = useSelectorApp(
    (state) => state.alerts?.notifications ?? EMPTY_NOTIFICATIONS
  );
  const { ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION } = useCreditActionCosts();
  const { resolvedLanguage } = useAdvertiserContentLanguage({
    preset: CONTENT_LANGUAGE_PRESET.TEMPLATES,
  });
  const [previewingKeys, setPreviewingKeys] = useState([]);
  const [isVariablesModalOpen, setIsVariablesModalOpen] = useState(false);
  const [previewColumns, setPreviewColumns] = useState([]);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewUrls, setPreviewUrls] = useState([]);
  const [previewVariables, setPreviewVariables] = useState([]);
  const inflightKeysRef = useRef(new Set());
  const runsRef = useRef(new Map());
  const listenersRef = useRef(new Map());
  const finishRunRef = useRef(() => {});
  const consumedPreviewRequestKeyRef = useRef("");
  const previewByProduceIDRef = useRef({});
  const previewScopeRef = useRef(TEMPLATE_EDITOR_PREVIEW_SCOPE.SLIDE);
  const saveQueueRef = useRef(Promise.resolve());

  const publishPreviewingKeys = useCallback(() => {
    setPreviewingKeys([...inflightKeysRef.current]);
  }, []);

  const claimPreviewKey = useCallback(
    (key) => {
      if (!canStartPreviewRun({ keys: inflightKeysRef.current, key })) {
        return false;
      }
      inflightKeysRef.current.add(key);
      publishPreviewingKeys();
      return true;
    },
    [publishPreviewingKeys]
  );

  const releasePreviewKey = useCallback(
    (key) => {
      if (!inflightKeysRef.current.delete(key)) return;
      publishPreviewingKeys();
    },
    [publishPreviewingKeys]
  );

  const stopProduceListener = useCallback(
    (produceID) => {
      const listenerRef = listenersRef.current.get(produceID);
      if (!listenerRef) return;
      listenersRef.current.delete(produceID);
      dispatch(
        stopListenerCreativeTemplateProduce({
          listenerRef,
        })
      );
    },
    [dispatch]
  );

  const showPreviewResult = useCallback(({ urls = [], variables = [] }) => {
    if (urls.length === 0) return;
    setPreviewUrl(urls[0]);
    setPreviewUrls(urls);
    setPreviewVariables(variables);
  }, []);

  const finishRun = useCallback(
    (
      produceID,
      { urls = [], url = "", message = "", produceRecord = null } = {}
    ) => {
      const run = runsRef.current.get(produceID);
      if (!run || run.settled) return;
      run.settled = true;
      runsRef.current.delete(produceID);
      stopProduceListener(produceID);
      releasePreviewKey(run.key);
      const pendingVariables = run.pendingVariables || [];
      const variables =
        pendingVariables.length > 0
          ? pendingVariables
          : listPreviewVariableEntries({
              rows: produceRecord?.rows,
              columns: produceRecord?.columns,
            });
      const requestedUrls =
        urls.length > 0 ? urls : url ? [url] : listPreviewUrls(produceRecord);
      const resolvedUrls = requestedUrls
        .map((item) => String(item || "").trim())
        .filter(Boolean);
      if (resolvedUrls.length > 0) {
        previewByProduceIDRef.current[produceID] = {
          url: resolvedUrls[0],
          urls: resolvedUrls,
          variables,
        };
        showPreviewResult({ urls: resolvedUrls, variables });
        return;
      }
      SimpleAlert({
        title: t(TEMPLATES_EDITOR_PREVIEW_ERROR),
        text: message || "",
        icon: "error",
      });
    },
    [releasePreviewKey, showPreviewResult, stopProduceListener, t]
  );

  finishRunRef.current = finishRun;

  const trySettleProduceRecord = useCallback((produceID, produceRecord) => {
    const run = runsRef.current.get(produceID);
    if (!run || run.settled) return false;
    const urls = listPreviewUrls(produceRecord);
    const expected = Math.max(
      Number(produceRecord?.artboardCount) || 0,
      Number(run.expectedCount) || 0,
      1
    );
    const terminal = isProduceRecordTerminal(produceRecord);
    if (urls.length >= expected || (terminal && urls.length > 0)) {
      finishRunRef.current(produceID, { urls, produceRecord });
      return true;
    }
    if (terminal) {
      finishRunRef.current(produceID, {
        message: produceRecord?.error || "",
        produceRecord,
      });
      return true;
    }
    return false;
  }, []);

  const attachProduceListener = useCallback(
    (produceID) => {
      dispatch(
        startListenerCreativeTemplateProduce({
          produceID,
          onUpdatedDataCallback: (value) => {
            trySettleProduceRecord(produceID, value);
          },
          onReferenceAvailable: (dbRef) => {
            const run = runsRef.current.get(produceID);
            if (!run || run.settled) {
              dispatch(
                stopListenerCreativeTemplateProduce({
                  listenerRef: dbRef,
                })
              );
              return;
            }
            listenersRef.current.set(produceID, dbRef);
          },
        })
      );
    },
    [dispatch, trySettleProduceRecord]
  );

  useEffect(() => {
    const listeners = listenersRef.current;
    const runs = runsRef.current;
    const keys = inflightKeysRef.current;
    return () => {
      listeners.forEach((listenerRef) => {
        dispatch(
          stopListenerCreativeTemplateProduce({
            listenerRef,
          })
        );
      });
      listeners.clear();
      runs.clear();
      keys.clear();
    };
  }, [dispatch]);

  useEffect(() => {
    const runs = [...runsRef.current.values()].filter((run) => !run.settled);
    if (runs.length === 0) return undefined;

    let cancelled = false;
    runs.forEach((run) => {
      const alert = findPreviewProduceAlert({
        notifications,
        produceID: run.produceID,
      });
      if (!alert || !TERMINAL_ALERT_STATUSES.has(alert.status)) return;
      dispatch(
        startGetCreativeTemplateProduce({
          produceID: run.produceID,
        })
      ).then((record) => {
        if (cancelled) return;
        const current = runsRef.current.get(run.produceID);
        if (!current || current.settled) return;
        if (trySettleProduceRecord(run.produceID, record)) return;
        if (alert.status === ALERTS_ASYNC_STATUS.ERROR) {
          finishRunRef.current(run.produceID, {
            message: alert.message || record?.error || "",
            produceRecord: record,
          });
        }
      });
    });

    return () => {
      cancelled = true;
    };
  }, [dispatch, notifications, previewingKeys, trySettleProduceRecord]);

  useEffect(() => {
    if (!initialPreviewProduceID) return;
    const requestKey = previewRequestKey || initialPreviewProduceID;
    if (consumedPreviewRequestKeyRef.current === requestKey) return;
    consumedPreviewRequestKeyRef.current = requestKey;

    const cached = previewByProduceIDRef.current[initialPreviewProduceID];
    if (cached?.url) {
      const urls = cached.urls?.length > 0 ? cached.urls : [cached.url];
      showPreviewResult({ urls, variables: cached.variables || [] });
      return;
    }

    if (runsRef.current.has(initialPreviewProduceID)) return;
    if (!claimPreviewKey(PREVIEW_RUN_KEY.RESTORE)) return;
    runsRef.current.set(initialPreviewProduceID, {
      produceID: initialPreviewProduceID,
      key: PREVIEW_RUN_KEY.RESTORE,
      expectedCount: 1,
      pendingVariables: [],
      settled: false,
    });
    attachProduceListener(initialPreviewProduceID);
  }, [
    attachProduceListener,
    claimPreviewKey,
    initialPreviewProduceID,
    previewRequestKey,
    showPreviewResult,
  ]);

  const collectColumns = useCallback(() => {
    const context = getPreviewContext?.() || {};
    return listProduceColumns({
      variables: context.variables || {},
      nodes: context.nodes || {},
      artboards: context.artboards || {},
      slides: context.slides || [],
      layoutKind: context.layoutKind,
      assets: context.assets || {},
      slideIDs: listPreviewSlideIDs({
        context,
        scope: previewScopeRef.current,
      }),
    });
  }, [getPreviewContext]);

  const enqueueSave = useCallback(() => {
    const run = saveQueueRef.current
      .catch(() => undefined)
      .then(() => saveTemplate?.());
    saveQueueRef.current = run.then(
      () => undefined,
      () => undefined
    );
    return run;
  }, [saveTemplate]);

  const startPreview = useCallback(
    async ({ row, columns, scope, runKey } = {}) => {
      const resolvedScope = resolvePreviewScope(
        scope || previewScopeRef.current
      );
      const context = getPreviewContext?.() || {};
      const key = runKey || previewRunKey({ scope: resolvedScope, context });
      if (isLocked || !claimPreviewKey(key)) return;
      const artboardID =
        context.activeArtboardID ||
        listArtboardIDs({ artboards: context.artboards })[0] ||
        "";
      const slideIDs = listPreviewSlideIDs({
        context,
        scope: resolvedScope,
      });
      // Continuous previews always run the whole strip (never per-slide scope)
      // and return one sliced image per slide.
      const continuousSlideCount = isContinuousLayout({
        layoutKind: context.layoutKind,
        slides: context.slides,
      })
        ? getProduceSlideCount({
            layoutKind: context.layoutKind,
            slides: context.slides,
            layoutConfigurations: context.layoutConfigurations,
          })
        : 0;
      const cellCount =
        continuousSlideCount > 0
          ? continuousSlideCount
          : slideIDs.length > 0
            ? slideIDs.length
            : 1;
      if (!templateID || !artboardID) {
        releasePreviewKey(key);
        SimpleAlert({
          title: t(TEMPLATES_PRODUCE_VALIDATION_NO_FORMATS),
          icon: "warning",
        });
        return;
      }

      setIsVariablesModalOpen(false);

      let produceID = "";
      try {
        const saved = await enqueueSave();
        if (!inflightKeysRef.current.has(key)) return;
        if (!saved?.ok) {
          releasePreviewKey(key);
          return;
        }

        const isBlocked = await dispatch(
          getIsBlockedActions({
            action: ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
            showUsageConfirmationAlert: true,
            multiplier: cellCount,
          })
        );
        if (!inflightKeysRef.current.has(key)) return;
        if (isBlocked) {
          releasePreviewKey(key);
          return;
        }

        const nextColumns = Array.isArray(columns) ? columns : [];
        const jobRows =
          nextColumns.length > 0 ? [row] : createVariationRows({ count: 1 });
        produceID = dispatch(getKeyDatabase());
        const language = getDefaultProduceLanguage({
          language: resolvedLanguage,
        });
        runsRef.current.set(produceID, {
          produceID,
          key,
          expectedCount: cellCount,
          pendingVariables: listPreviewVariableEntries({
            rows: jobRows,
            columns: nextColumns,
          }),
          settled: false,
        });
        attachProduceListener(produceID);

        const result = await dispatch(
          startProduceCreativeTemplate({
            templateID,
            produceID,
            language,
            rows: jobRows,
            jobRows,
            jobVariationCount: 1,
            columns: nextColumns,
            artboardIDs: [artboardID],
            ...(slideIDs.length > 0 ? { slideIDs } : {}),
            cellCount,
            variationCount: 1,
            intent: CREATIVE_TEMPLATE_PRODUCE_INTENT.PREVIEW,
          })
        );
        const run = runsRef.current.get(produceID);
        if (!run || run.settled) return;
        if (!result?.ok) {
          finishRunRef.current(produceID, {});
        }
      } catch {
        const run = produceID ? runsRef.current.get(produceID) : null;
        if (run && !run.settled) {
          finishRunRef.current(produceID, {});
          return;
        }
        releasePreviewKey(key);
      }
    },
    [
      ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
      attachProduceListener,
      claimPreviewKey,
      dispatch,
      enqueueSave,
      getPreviewContext,
      isLocked,
      releasePreviewKey,
      resolvedLanguage,
      t,
      templateID,
    ]
  );

  const onGeneratePreview = useCallback(
    (scope = TEMPLATE_EDITOR_PREVIEW_SCOPE.SLIDE) => {
      if (isLocked) return;
      const resolvedScope = resolvePreviewScope(scope);
      const context = getPreviewContext?.() || {};
      const key = previewRunKey({ scope: resolvedScope, context });
      if (!canStartPreviewRun({ keys: inflightKeysRef.current, key })) return;
      previewScopeRef.current = resolvedScope;
      const columns = collectColumns();
      if (columns.length > 0) {
        setPreviewColumns(columns);
        setIsVariablesModalOpen(true);
        return;
      }
      startPreview({
        row: createVariationRows({ count: 1 })[0],
        columns: [],
        scope: resolvedScope,
        runKey: key,
      });
    },
    [collectColumns, getPreviewContext, isLocked, startPreview]
  );

  const onCloseVariablesModal = useCallback(() => {
    setIsVariablesModalOpen(false);
  }, []);

  const onSubmitVariables = useCallback(
    ({ row } = {}) => {
      const context = getPreviewContext?.() || {};
      const scope = previewScopeRef.current;
      startPreview({
        row,
        columns: previewColumns,
        scope,
        runKey: previewRunKey({ scope, context }),
      });
    },
    [getPreviewContext, previewColumns, startPreview]
  );

  const onClosePreviewImage = useCallback(() => {
    setPreviewUrl("");
    setPreviewUrls([]);
    setPreviewVariables([]);
  }, []);

  return {
    previewingKeys,
    isVariablesModalOpen,
    previewColumns,
    previewUrl,
    previewUrls,
    previewVariables,
    onGeneratePreview,
    onCloseVariablesModal,
    onSubmitVariables,
    onClosePreviewImage,
  };
};

export default useTemplateEditorPreview;
